import { useEffect, useState } from "react";
import { Truck, IndianRupee, Users, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * TransportMonthlySummary — Phase 5.1, HOS dashboard ROI widget.
 *
 * Shows a 3-column breakdown of Transportation requisitions completed in the
 * current calendar month, split by `payment_source` (school / student imprest
 * / parents). Numeric sums best-effort parse the alphanumeric approx-price;
 * requisitions whose price is text-only are counted but excluded from the sum
 * and surfaced in a footnote.
 *
 * Renders nothing while loading or on error; caller decides visibility.
 */
const BUCKETS = [
  { key: "school",          label: "School will pay",     color: "#2d6b6b" },
  { key: "student_imprest", label: "Student Imprest",     color: "#7a1f2b" },
  { key: "parents",         label: "Parents will pay",    color: "#b48a45" },
];

const fmtINR = (n) => {
  if (!n) return "₹ 0";
  return `₹ ${Math.round(n).toLocaleString("en-IN")}`;
};

export default function TransportMonthlySummary() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/transport-monthly-summary");
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="sheet p-5 mb-4 animate-pulse" data-testid="transport-summary-loading">
        <div className="h-4 w-64 bg-[#e2d7c3] rounded" />
        <div className="mt-4 grid grid-cols-3 gap-3">
          {[0,1,2].map((i) => (
            <div key={i} className="h-24 bg-[#faf5eb] rounded border border-[#e2d7c3]" />
          ))}
        </div>
      </div>
    );
  }

  if (err) {
    // Silently hide if role-gated; only show noise if it's an actual server error.
    if (err.toLowerCase().includes("403") || err.toLowerCase().includes("hos")) return null;
    return (
      <div className="sheet p-5 mb-4 border-l-4 border-l-[#b3261e]" data-testid="transport-summary-error">
        <div className="flex items-center gap-2 text-sm text-[#b3261e]">
          <AlertCircle size={14} /> {err}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { month, breakdown, totals } = data;
  const monthLabel = (() => {
    const [y, m] = month.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleString("default",
                { month: "long", year: "numeric" });
  })();

  return (
    <div className="sheet p-5 mb-4" data-testid="transport-monthly-summary">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
        <h3 className="font-serif text-base font-semibold text-[#7a1f2b] inline-flex items-center gap-2">
          <Truck size={16} /> Transportation Bookings This Month
        </h3>
        <span className="text-xs text-[#6e7770] font-mono">{monthLabel}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {BUCKETS.map((b) => {
          const row = breakdown[b.key] || { count: 0, sum: 0, text_only_count: 0 };
          return (
            <div key={b.key}
                 className="rounded border p-3 bg-white"
                 style={{ borderLeftColor: b.color, borderLeftWidth: 3 }}
                 data-testid={`transport-bucket-${b.key}`}>
              <div className="text-[10px] uppercase tracking-widest font-semibold text-[#6e7770]">
                {b.label}
              </div>
              <div className="flex items-baseline justify-between mt-1">
                <div className="font-serif text-2xl font-semibold text-[#1f2320]"
                     data-testid={`transport-bucket-${b.key}-count`}>
                  {row.count}
                </div>
                <Users size={14} className="text-[#6e7770]" />
              </div>
              <div className="text-sm text-[#4a524c] mt-1 inline-flex items-center gap-1"
                   data-testid={`transport-bucket-${b.key}-sum`}>
                <IndianRupee size={12} /> {fmtINR(row.sum).replace("₹ ", "")}
              </div>
              {row.text_only_count > 0 && (
                <div className="text-[10px] text-[#b48a45] mt-1"
                     data-testid={`transport-bucket-${b.key}-text-only`}>
                  · {row.text_only_count} price{row.text_only_count === 1 ? "" : "s"} text-only
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-3 pt-3 border-t border-[#e2d7c3] flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
        <span className="font-serif text-[#1f2320]">
          Total: <strong data-testid="transport-total-count">{totals.count}</strong> bookings
        </span>
        <span className="text-[#2d6b6b]">
          Numeric sum: <strong data-testid="transport-total-sum">{fmtINR(totals.sum)}</strong>
        </span>
        {totals.text_only_count > 0 && (
          <span className="text-[#b48a45] text-xs"
                data-testid="transport-total-text-only">
            {totals.text_only_count} booking{totals.text_only_count === 1 ? "" : "s"}{" "}
            with text-only prices excluded from sum
          </span>
        )}
      </div>
    </div>
  );
}
